import {
  QuestionType_Enum_,
  useGetAssessmentDataQuery,
  useGetAssessmentMetricsQuery,
  useGetDatapointsTasksQuery,
  useGetRequiredAssessmentMaterialMetricsQuery,
  useUserCompaniesWithSustainabilityAssessmentsQuery,
  useGetDatapointMdrmTasksQuery,
  useGetMetricsDisclosureRequirementsQuery,
  GetMetricsDisclosureRequirementsQuery_,
  useGetCompanyUsersQuery,
  useGetAssessmentDisclosureRequirementsQuery,
  MetricDisclosureDetailsFragment_,
} from 'models/index.ts';
import { Task, TaskListRow, TaskListStatus } from '../components/TaskListTable.tsx';
import {
  FrequencyEnums,
  TimePeriodsEnums,
  QUARTERS_ARRAY,
  getTagsCombinationArray,
} from 'containers/Esrs/index.tsx';
import {
  mfStandardRefs,
  getTagsCombination,
  NOTAGCOMBINATION,
  createTaskReference,
} from 'containers/Esrs/utils.ts';
import { SelectedAssessmentOption } from '../components/TaskManagement.tsx';
import { useAuth } from 'Providers/auth/AuthProvider.tsx';
import { DataCollectionLevel } from 'containers/Esrs/pieces/DataCollection/DataCollection.d.tsx';
import { useMemo } from 'react';
import { getLatestSRAssessmentDate } from 'containers/Home/Home.hooks.ts';
import { TaskFilterOptions } from 'containers/Esrs/pieces/DisclosureRequirements/Metrics/MetricsTable/MetricsTable.utils.tsx';
import { useParams } from 'react-router-dom';

export type TaskListSortBy = 'standard' | 'deadline' | 'status' | 'owner' | 'businessUnit';

interface TaskListDataFilters {
  selectedAssessment?: SelectedAssessmentOption;
  taskFilterOption?: TaskFilterOptions;
  selectedMemberId?: string;
  searchQuery?: string;
  sortBy: TaskListSortBy;
  sortDirection: 'asc' | 'desc';
}
export interface TaskCompanyOption {
  id: string;
  name: string;
  assessments: { id: string; name: string; frameworkRef: string; lastEdited: any }[];
}

const statusOrder = {
  [TaskListStatus.IN_PROGRESS]: 0,
  [TaskListStatus.NOT_STARTED]: 1,
  [TaskListStatus.COMPLETED]: 2,
};

const getMetricDisclosureRequirement = (
  metric: MetricDisclosureDetailsFragment_ | undefined,
  frameworkRef: string | undefined
) => {
  const metricDR = metric?.requirement;
  const relatedDR = metric?.mfDisclosureRequirements
    ?.flatMap((mfDr) =>
      mfDr.mfDisclosureRequirement.drToMFDisclosureRequirements.map(
        (dr) => dr.disclosureRequirement
      )
    )
    .find((dr) => dr.frameworkRef === frameworkRef);
  const metricMfDR = metric?.mfDisclosureRequirements?.[0]?.mfDisclosureRequirement;

  const disclosureRequirement =
    metricDR?.frameworkRef === frameworkRef
      ? {
          reference: metricDR?.reference,
          title: metricDR?.title,
          type: metricDR?.type,
          order: metricDR?.order,
        }
      : relatedDR
        ? {
            reference: relatedDR.reference,
            title: relatedDR.title,
            type: relatedDR.type,
            order: relatedDR.order,
          }
        : {
            reference: metricMfDR?.reference,
            title: metricMfDR?.title,
            type: metricMfDR?.type,
            order: metricMfDR?.order,
          };

  return disclosureRequirement;
};

const sortByDrOrder = (a: TaskListRow, b: TaskListRow) => {
  const A = a.task?.drOrder != null ? Number(a.task.drOrder) : Number.POSITIVE_INFINITY;
  const B = b.task?.drOrder != null ? Number(b.task.drOrder) : Number.POSITIVE_INFINITY;
  return A - B;
};
const sortByDate = (a: TaskListRow, b: TaskListRow) => {
  const aValue = a.task?.dueDate ? new Date(a.task.dueDate).getTime() : Infinity;
  const bValue = b.task?.dueDate ? new Date(b.task.dueDate).getTime() : Infinity;
  if (aValue === bValue) return 0;
  return aValue < bValue ? -1 : 1;
};
const sortByStatus = (a: TaskListRow, b: TaskListRow) => {
  const aKey = String(a.task?.status ?? '').trim();
  const bKey = String(b.task?.status ?? '').trim();
  const aValue = statusOrder[aKey as keyof typeof statusOrder] ?? 99;
  const bValue = statusOrder[bKey as keyof typeof statusOrder] ?? 99;
  return aValue - bValue;
};
const sortByOwner = (a: TaskListRow, b: TaskListRow) => {
  const getOwnerName = (r: TaskListRow) => {
    const owner = r.task?.owner as any;
    if (!owner) return '';
    if (typeof owner === 'string') return owner;
    return owner.displayName ?? '';
  };
  const aName = getOwnerName(a).toLowerCase();
  const bName = getOwnerName(b).toLowerCase();
  return aName.localeCompare(bName);
};

const sortingPriorities: Record<
  TaskListSortBy,
  Array<(a: TaskListRow, b: TaskListRow) => number>
> = {
  standard: [sortByDrOrder, sortByDate, sortByStatus, sortByOwner],
  owner: [sortByOwner, sortByStatus, sortByDate, sortByDrOrder],
  deadline: [sortByDate, sortByStatus, sortByOwner, sortByDrOrder],
  status: [sortByStatus, sortByDate, sortByOwner, sortByDrOrder],
  businessUnit: [sortByStatus, sortByDate, sortByOwner, sortByDrOrder],
};

// function to sort tasks while preserving subtitles
const sortTasks = (rows: TaskListRow[], sortBy: TaskListSortBy, isHomepage?: boolean) => {
  const result: TaskListRow[] = [];
  const buffer: Array<{ row: TaskListRow; idx: number }> = [];
  const comps = sortingPriorities[sortBy] ?? sortingPriorities.standard;
  const flush = () => {
    if (!buffer.length) return;
    buffer.sort((aWrap, bWrap) => {
      const a = aWrap.row,
        b = bWrap.row;
      for (const cmp of comps) {
        const r = cmp(a, b);
        if (r !== 0) return r;
      }

      return aWrap.idx - bWrap.idx;
    });
    result.push(...buffer.map((x) => x.row));
    buffer.length = 0;
  };

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.subtitle) {
      flush();
      result.push(row);
    } else {
      if (isHomepage) flush();
      buffer.push({ row, idx: i });
    }
  }
  flush();
  return result;
};

export const useTaskListData = (filters: TaskListDataFilters, isHomepage?: boolean) => {
  const companyId = filters.selectedAssessment?.companyData.id;
  const assessmentId = filters.selectedAssessment?.id;
  const frameworkRef = filters.selectedAssessment?.frameworkRef;
  const {
    tasks,
    user,
    loading: taskLoading,
    error: taskError,
  } = useGetAssessmentTasks(
    filters.selectedAssessment?.id,
    filters.selectedAssessment?.frameworkRef,
    filters.selectedAssessment?.companyData.id
  );

  const { data: allAssessmentDrsData } = useGetAssessmentDisclosureRequirementsQuery({
    variables: { assessmentId: filters.selectedAssessment?.id },
    skip: !filters.selectedAssessment,
  });

  const { taskList, userId } = useMemo(() => {
    const currentUserId = user?.id ?? '';
    let list = tasks ?? [];

    if (filters) {
      const { searchQuery, sortBy, sortDirection, taskFilterOption, selectedMemberId } = filters;

      // 1) Filter by owner / members
      if (taskFilterOption === TaskFilterOptions.yourTasks && currentUserId) {
        list = list.filter((task) => task.owner?.id === currentUserId);
      }

      if (taskFilterOption === TaskFilterOptions.members && selectedMemberId) {
        list = list.filter((task) => task.owner?.id === selectedMemberId);
      }

      if (taskFilterOption === TaskFilterOptions.unassigned) {
        list =
          allAssessmentDrsData?.EsrsAssessment_by_pk?.materialStandards?.flatMap((std) =>
            std.materialMetrics
              ?.filter(
                (mm) =>
                  !mm.metric.answers.length ||
                  mm.metric.answers.every((ans) => !ans.datapoints.length) ||
                  mm.metric.answers.some((ans) => ans.datapoints.some((dp) => !dp.taskId))
              )
              ?.map((mm) => {
                const dr = getMetricDisclosureRequirement(mm.metric, frameworkRef);
                const link = `${companyId}/sustainability-reporting/${frameworkRef}/${assessmentId}/standard/${std.standard.reference}/disclosure-requirement/${dr.type}/${dr.reference}`;
                const buLink = `${companyId}/sustainability-reporting/${frameworkRef}/${assessmentId}/bu-standard/${std.standard.reference}/bu/${dr.type}/disclosure-requirement/${dr.reference}`;
                return {
                  id: dr.reference ?? '',
                  title: dr.title ?? '',
                  reference: dr.reference ?? '',
                  status: TaskListStatus.NOT_STARTED,
                  drOrder: dr.order,
                  metricType:
                    mm.metric?.metricType === QuestionType_Enum_.Decimal_
                      ? 'quantitative'
                      : 'narrative',
                  link: mm.dataCollection === DataCollectionLevel.company ? link : buLink,
                  standardTitle: std.standard.title,
                  standardReference: std.standard.reference,
                  standardOrder: std.standard.order,
                  reportingUnitName:
                    mm.dataCollection === DataCollectionLevel.company
                      ? ''
                      : mm.metric.answers?.[0]?.reportingUnit.name,
                };
              })
          ) ?? [];
      }

      // 2) Search filter
      if (searchQuery && searchQuery.trim() !== '') {
        const q = searchQuery.toLowerCase();
        list = list.filter((task: any) => {
          const taskDueDate = task.dueDate ? new Date(task.dueDate) : undefined;

          const dueDateMatch = taskDueDate
            ? taskDueDate
                .toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
                .toLowerCase()
                .includes(q)
            : false;

          return (
            task.title.toLowerCase().includes(q) ||
            task.reference.toLowerCase().includes(q) ||
            task.owner.displayName.toLowerCase().includes(q) ||
            dueDateMatch ||
            (task.companyName && task.companyName.toLowerCase().includes(q)) ||
            (task.assessmentName && task.assessmentName.toLowerCase().includes(q))
          );
        });
      }

      // 3) Sorting
      if (sortBy) {
        const sortKeys = [
          'dueDate',
          'status',
          'owner.displayName',
          'standardReference',
          'reportingUnitName',
        ];

        const selectedKey =
          sortBy === 'deadline'
            ? 'dueDate'
            : sortBy === 'status'
              ? 'status'
              : sortBy === 'owner'
                ? 'owner.displayName'
                : sortBy === 'standard'
                  ? 'standardReference'
                  : sortBy === 'businessUnit'
                    ? 'reportingUnitName'
                    : sortBy;

        const keys = [selectedKey, ...sortKeys.filter((k) => k !== selectedKey)];

        const getValue = (obj: any, path: string) =>
          path.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);

        list = [...list].sort((a: any, b: any) => {
          for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            let aValue = getValue(a, key);
            let bValue = getValue(b, key);
            const dir = i === 0 ? sortDirection : 'asc';

            if (key === 'status') {
              aValue = statusOrder[aValue as keyof typeof statusOrder] ?? 99;
              bValue = statusOrder[bValue as keyof typeof statusOrder] ?? 99;
            }

            if (key === 'dueDate') {
              aValue = aValue ? new Date(aValue).getTime() : Infinity;
              bValue = bValue ? new Date(bValue).getTime() : Infinity;
            }

            if (aValue < bValue) return dir === 'asc' ? -1 : 1;
            if (aValue > bValue) return dir === 'asc' ? 1 : -1;
          }
          return 0;
        });
      }
    }

    // 4) Group similar tasks
    const groupedTaskMap = new Map<
      string,
      Task & {
        addedCount: number;
        totalCount: number;
        completedCount: number;
        inProgressCount: number;
        notStartedCount: number;
        extraInfo: string;
        taskIds: string[];
        metricTypeCounts?: { narrative: number; quantitative: number };
      }
    >();

    for (const task of list) {
      const key = [
        task.owner?.id,
        task.reference,
        task.title,
        task.dueDate ? new Date(task.dueDate).getTime() : 'no-date',
        task.reportingUnitName || 'no-reporting-unit',
      ].join('|');

      if (!groupedTaskMap.has(key)) {
        groupedTaskMap.set(key, {
          ...task,
          addedCount: 0,
          totalCount: 0,
          completedCount: 0,
          inProgressCount: 0,
          notStartedCount: 0,
          extraInfo: '',
          taskIds: [],
          metricTypeCounts: { narrative: 0, quantitative: 0 },
        });
      }

      const grouped = groupedTaskMap.get(key)!;

      grouped.taskIds.push(task.id);
      grouped.totalCount++;

      if (task.status === TaskListStatus.COMPLETED || task.status === TaskListStatus.IN_PROGRESS) {
        grouped.addedCount++;
      }
      if (task.status === TaskListStatus.COMPLETED) {
        grouped.completedCount++;
      } else if (task.status === TaskListStatus.IN_PROGRESS) {
        grouped.inProgressCount++;
      } else if (task.status === TaskListStatus.NOT_STARTED) {
        grouped.notStartedCount++;
      }

      if (task.metricType === 'quantitative') {
        grouped.metricTypeCounts!.quantitative++;
      } else {
        grouped.metricTypeCounts!.narrative++;
      }
    }

    // compute grouped status ONCE (moved out of loop)
    for (const grouped of groupedTaskMap.values()) {
      if (grouped.completedCount === grouped.totalCount) {
        grouped.status = TaskListStatus.COMPLETED;
      } else if (grouped.addedCount > 0) {
        grouped.status = TaskListStatus.IN_PROGRESS;
      } else {
        grouped.status = TaskListStatus.NOT_STARTED;
      }
    }

    const groupedTasks = Array.from(groupedTaskMap.values());

    // 5) Build rows with subtitles
    const groupedRows: TaskListRow[] = [];

    let getSubtitle: (task: Task) => string;

    if (filters && filters.sortBy === 'businessUnit') {
      getSubtitle = (task) => {
        if (!task.reportingUnitName) return 'Company level';
        return task.reportingUnitName;
      };
    } else if (filters && filters.sortBy === 'standard') {
      getSubtitle = (task) => task.standardTitle || '';
    } else if (filters && filters.sortBy === 'deadline') {
      getSubtitle = (task) => {
        if (!task.dueDate) return 'No due date';
        const date = new Date(task.dueDate);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        });
      };
    } else if (filters && filters.sortBy === 'status') {
      getSubtitle = (task) => task.status;
    } else if (filters && filters.sortBy === 'owner') {
      getSubtitle = (task) => task.owner?.displayName || 'No owner';
    } else {
      getSubtitle = () => '';
    }

    const subtitleMap = new Map<string, Task[]>();
    for (const task of groupedTasks) {
      const subtitle = getSubtitle(task);
      if (!subtitleMap.has(subtitle)) {
        subtitleMap.set(subtitle, []);
      }
      subtitleMap.get(subtitle)!.push(task);
    }

    for (const [subtitle, tasksGroup] of subtitleMap.entries()) {
      if (subtitle && !isHomepage) {
        groupedRows.push({ task: null, subtitle: subtitle.toUpperCase() });
      }

      const nonCompleted = tasksGroup.filter((t) => t.status !== TaskListStatus.COMPLETED);
      const completed = tasksGroup.filter((t) => t.status === TaskListStatus.COMPLETED);

      for (const task of nonCompleted) {
        groupedRows.push({ task, subtitle: null });
      }
      for (const task of completed) {
        groupedRows.push({ task, subtitle: null });
      }
    }

    const sortedRows = sortTasks(groupedRows, filters.sortBy, isHomepage);

    return {
      taskList: isHomepage ? sortedRows.slice(0, 5) : sortedRows,
      userId: currentUserId,
    };
  }, [tasks, filters, user?.id, allAssessmentDrsData]);

  return {
    taskList,
    userId,
    loading: taskLoading,
    error: taskError,
  };
};

export const useGetAssessmentTasks = (
  assessmentId?: string,
  frameworkRef?: string,
  companyId?: string
) => {
  const { user } = useAuth();
  const { data: companyUsers, loading: companyUsersLoading } = useGetCompanyUsersQuery({
    variables: { companyId },
    skip: !companyId,
  });

  const {
    data: datapointTasksQueryData,
    loading: datapointTasksLoading,
    error: datapointTasksError,
  } = useGetDatapointsTasksQuery({
    variables: { assessmentId },
    skip: !assessmentId,
  });

  const {
    data: datapointMdrmTasksQueryData,
    loading: datapointMdrmTasksLoading,
    error: datapointMdrmTasksError,
  } = useGetDatapointMdrmTasksQuery({
    variables: { assessmentId },
    skip: !assessmentId,
  });

  const { data, loading, error } = useGetAssessmentDataQuery({
    variables: {
      esrsAssessmentId: assessmentId,
    },
    skip: !assessmentId,
  });

  const companyLevelReportingUnitId = useMemo(
    () => data?.EsrsAssessment_by_pk?.reportingUnits?.find((ru) => ru.isCompanyLevel)?.id,
    [data]
  );

  const assessmentReportingUnits = useMemo(
    () =>
      data?.EsrsAssessment_by_pk?.reportingUnits
        ?.filter((ru) => !ru.isCompanyLevel)
        ?.map((ru) => ru.id),
    [data]
  );

  const parentAssessmentId = data?.EsrsAssessment_by_pk?.parentAssessmentId;

  // required metric references
  const {
    data: requiredMetrics,
    loading: requiredMetricsLoading,
    error: requiredMetricsError,
  } = useGetRequiredAssessmentMaterialMetricsQuery({
    variables: {
      assessmentId: assessmentId,
      parentAssessmentId: parentAssessmentId || assessmentId,
    },
    skip: !assessmentId,
  });

  const requiredMetricRefs = requiredMetrics?.esrs_MaterialMetric.map((m) => m.metricRef) ?? [];

  // relevant metrics with children
  const {
    data: metricsData,
    loading: metricsLoading,
    error: metricsError,
  } = useGetAssessmentMetricsQuery({
    variables: {
      assessmentId: assessmentId,
      requiredMetrics: requiredMetricRefs,
    },
    skip: !assessmentId || !requiredMetricRefs?.length,
  });

  const createDatapointObject = (data: {
    metricRef: string;
    timeframe: string;
    reportingUnitId: string;
    tags: {
      tagType: string;
      tagValue: string;
    }[];
    disclosureRequirementRef: string;
    tagCombination: string;
  }) => ({
    metricRef: data.metricRef,
    timeframe: data.timeframe,
    tagCombination: data.tagCombination,
    disclosureRequirementRef: data.disclosureRequirementRef,
    tags: data.tags,
    reportingUnitId: data.reportingUnitId,
  });

  // heavy metric expansion memoized
  const metrics: {
    tagCombination: string;
    metricRef: string;
    timeframe: string;
    disclosureRequirementRef: string;
    tags: {
      tagType: string;
      tagValue: string;
    }[];
    reportingUnitId: string;
  }[] = useMemo(
    () =>
      metricsData?.EsrsMetric.flatMap((metric) => {
        // metric data
        const materialMetric = metric.materialMetrics?.[0];
        const materialStandardRef = materialMetric?.materialStandard?.standardRef;
        const disclosureRequirementRef = mfStandardRefs.includes(materialStandardRef)
          ? metric.mfDisclosureRequirements?.[0]?.mfDisclosureRequirementRef
          : metric.disclosureRequirementRef; // dr - could be mfDR

        const isParentMetric = metric.view_childrenMetrics.length > 0;
        const timeframes =
          materialMetric?.frequency === FrequencyEnums.yearly
            ? [TimePeriodsEnums.year]
            : QUARTERS_ARRAY;

        const isCollectedOnBU =
          materialMetric?.dataCollection === DataCollectionLevel.reportingUnits;

        const getReportingUnitIds = (isBU: boolean) => {
          if (isBU && assessmentReportingUnits?.length) {
            // include company level for aggregated metrics
            return [...assessmentReportingUnits, companyLevelReportingUnitId].filter(
              Boolean
            ) as string[];
          }
          return companyLevelReportingUnitId ? [companyLevelReportingUnitId] : [];
        };

        const tags = materialMetric?.materialMetricTags ?? [];
        const { allTagsArray } = getTagsCombinationArray(tags);
        const tagCombinations = allTagsArray.map((tagsArray) => ({
          tags: tagsArray,
          tagCombination: getTagsCombination(tagsArray),
        }));

        //   parent metric tags
        const isChildMetric = metric.parentMetrics.length > 0;
        const parentMaterialMetric = metricsData?.EsrsMetric.find((m) =>
          metric.parentMetrics.some((p) => p.parentMetricRef === m.reference)
        )?.materialMetrics?.[0];

        const parentHasTags = parentMaterialMetric?.materialMetricTags?.some(
          (m) => !!m.materialTagValues?.length
        );

        const parentTagCombinations = getTagsCombinationArray(
          parentMaterialMetric?.materialMetricTags ?? []
        ).allTagsArray.map((tagsArray) => ({
          tags: tagsArray,
          tagCombination: getTagsCombination(tagsArray),
        }));

        //   final tags - considering parent metric
        const finalTags = isChildMetric && parentHasTags ? parentTagCombinations : tagCombinations;

        if (isParentMetric) {
          const leafChildren = metricsData.EsrsMetric.filter(
            (m) =>
              metric.view_childrenMetrics.some((cm) => cm.childMetricRef === m.reference) &&
              m.view_childrenMetrics.length === 0
          );

          const areAllChildrenQuarterly = leafChildren.every(
            (m) => m.materialMetrics?.[0].frequency === FrequencyEnums.quarterly
          );

          const hasBUChild = leafChildren.some(
            (m) => m.materialMetrics?.[0]?.dataCollection === DataCollectionLevel.reportingUnits
          );

          if (areAllChildrenQuarterly) {
            return QUARTERS_ARRAY.flatMap((timeframe) =>
              getReportingUnitIds(hasBUChild).map((ru) =>
                createDatapointObject({
                  metricRef: metric.reference,
                  timeframe,
                  reportingUnitId: ru,
                  tags: [],
                  disclosureRequirementRef,
                  tagCombination: NOTAGCOMBINATION,
                })
              )
            );
          }
          return getReportingUnitIds(hasBUChild).map((ru) =>
            createDatapointObject({
              metricRef: metric.reference,
              timeframe: TimePeriodsEnums.year,
              tagCombination: NOTAGCOMBINATION,
              disclosureRequirementRef,
              tags: [],
              reportingUnitId: ru,
            })
          );
        }

        const datapoints = timeframes
          .flatMap((timeframe) => {
            const reportingUnitIds = getReportingUnitIds(isCollectedOnBU);

            const metricRow = reportingUnitIds.map((ru) =>
              createDatapointObject({
                metricRef: metric.reference,
                timeframe: timeframe,
                tagCombination: NOTAGCOMBINATION,
                disclosureRequirementRef,
                tags: [],
                reportingUnitId: ru,
              })
            );
            const metricTagRows = finalTags.flatMap((combo) =>
              reportingUnitIds.map((ru) =>
                createDatapointObject({
                  tagCombination: combo.tagCombination,
                  metricRef: metric.reference,
                  timeframe: timeframe,
                  disclosureRequirementRef,
                  tags: combo.tags,
                  reportingUnitId: ru,
                })
              )
            );

            // No tags case
            if (!finalTags?.length) return metricRow;

            return [metricRow, ...metricTagRows];
          })
          .flat();
        return datapoints;
      }) ?? [],
    [metricsData, assessmentReportingUnits, companyLevelReportingUnitId]
  );

  // memoize taskReferences
  const taskReferences = useMemo(
    () =>
      assessmentId
        ? metrics?.flatMap((metric) =>
            createTaskReference({
              metricRef: metric.metricRef,
              assessmentId,
              reportingUnitId: metric.reportingUnitId,
              tagCombination: metric.tagCombination ?? '',
              timeframe: metric.timeframe,
            })
          )
        : [],
    [assessmentId, metrics]
  );

  // use Set instead of includes
  const taskReferenceSet = useMemo(() => new Set(taskReferences), [taskReferences]);

  const datapointTasks = useMemo(
    () =>
      datapointTasksQueryData?.Task?.filter((task) => {
        if (!task.taskReference || !taskReferenceSet.has(task.taskReference)) return false;
        return true;
      })?.map((task) => ({
        ...task,
        datapoint: task?.datapoints?.[0],
      })) ?? [],
    [datapointTasksQueryData, taskReferenceSet]
  );

  const datapointMdrmTasks = useMemo(
    () =>
      datapointMdrmTasksQueryData?.Task?.filter((task) => {
        if (!task.taskReference || !task.datapointMdrm?.datapoint?.isMdrmDisclosed) return false;
        return true;
      })?.map((task) => ({
        ...task,
        datapoint: task?.datapointMdrm?.datapoint,
      })) ?? [],
    [datapointMdrmTasksQueryData]
  );

  const tasks = useMemo(
    () => [...datapointTasks, ...datapointMdrmTasks],
    [datapointTasks, datapointMdrmTasks]
  );

  const taskMetrics = useMemo(
    () =>
      tasks
        .map((task) => task.datapoint?.answer.metric.reference)
        .filter((t): t is string => !!t) ?? [],
    [tasks]
  );

  const { data: metricsDRs, loading: metricsDRLoading } = useGetMetricsDisclosureRequirementsQuery({
    variables: {
      metricRefs: taskMetrics,
    },
    skip: !taskMetrics.length,
  });

  const tasksData: Task[] = useMemo(
    () =>
      tasks.map((task) => {
        const datapoint = task.datapoint;
        const metric = datapoint?.answer.metric;
        const metricDisclosuresData = metricsDRs?.EsrsMetric?.find(
          (m) => m.reference === metric?.reference
        );
        const standardTitle = metric?.materialMetrics?.[0]?.materialStandard.standard.title;
        const standardReference = metric?.materialMetrics?.[0]?.materialStandard.standardRef;
        const standardOrder = metric?.materialMetrics?.[0]?.materialStandard.standard.order;
        const standardRef = metric?.materialMetrics?.[0]?.materialStandard.standardRef;
        const reportingUnitId = datapoint?.answer.reportingUnit.id;
        const reportingUnitName = datapoint?.answer.reportingUnit.name;
        const disclosureRequirement = getMetricDisclosureRequirement(
          metricDisclosuresData,
          frameworkRef
        );

        let mappedStatus: TaskListStatus = TaskListStatus.NOT_STARTED;
        if (task.status === 'IN_PROGRESS') mappedStatus = TaskListStatus.IN_PROGRESS;
        else if (task.status === 'COMPLETED') mappedStatus = TaskListStatus.COMPLETED;
        else if (task.status === 'NOT_STARTED') mappedStatus = TaskListStatus.NOT_STARTED;

        const link = `${companyId}/sustainability-reporting/${frameworkRef}/${assessmentId}/standard/${standardRef}/disclosure-requirement/${disclosureRequirement.type}/${disclosureRequirement.reference}`;
        const buLink = `${companyId}/sustainability-reporting/${frameworkRef}/${assessmentId}/bu-standard/${standardRef}/bu/${reportingUnitId}/disclosure-requirement/${disclosureRequirement.reference}`;

        const owner = companyUsers?.CompanyUser.find(
          (cUser) => task.ownerId === cUser.user.id
        )?.user;

        return {
          id: task.id,
          owner: {
            avatarUrl: owner?.avatarUrl ?? '',
            id: task.ownerId,
            email: owner?.email ?? '',
            displayName: owner?.displayName ?? '',
          },
          title: disclosureRequirement.title ?? '',
          reference: disclosureRequirement.reference ?? '',
          drOrder: disclosureRequirement.order,
          status: mappedStatus,
          dueDate: task.dueDate,
          metricType:
            metric?.metricType === QuestionType_Enum_.Decimal_ ? 'quantitative' : 'narrative',
          link: reportingUnitName === 'Company level' ? link : buLink,
          standardTitle,
          standardReference,
          standardOrder,
          reportingUnitName: reportingUnitName === 'Company level' ? '' : reportingUnitName,
        };
      }),
    [tasks, frameworkRef, companyId, assessmentId, metricsDRs]
  );

  return {
    tasks: tasksData,
    user,
    loading:
      requiredMetricsLoading ||
      loading ||
      metricsLoading ||
      datapointMdrmTasksLoading ||
      datapointTasksLoading ||
      metricsDRLoading ||
      companyUsersLoading,
    error:
      datapointMdrmTasksError ||
      datapointTasksError ||
      requiredMetricsError ||
      error ||
      metricsError,
  };
};

export const useGetCompanyOptions = () => {
  const { user } = useAuth();
  const { data, loading } = useUserCompaniesWithSustainabilityAssessmentsQuery({
    variables: { id: user?.id },
    skip: !user?.id,
    fetchPolicy: 'network-only',
  });

  const companyOptions: TaskCompanyOption[] =
    data?.data?.companies?.map((c) => ({
      id: c.company.id,
      name: c.company.name,
      assessments: (c.company.esrsAssessments || [])
        .map((a) => ({
          id: a.id,
          name: a.reportingYear ? `${a.frameworkRef?.toUpperCase()} ${a.reportingYear}` : a.id,
          frameworkRef: a.frameworkRef ?? ' ',
          lastEdited: getLatestSRAssessmentDate(a),
        }))
        .sort((a, b) => {
          const dateA = new Date(a.lastEdited ?? 0);
          const dateB = new Date(b.lastEdited ?? 0);
          return dateB.getTime() - dateA.getTime();
        }),
    })) ?? [];
  return { companyOptions, loading };
};
